/* eslint-disable react/no-array-index-key */
import React, { PureComponent, FormEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Type,
  ButtonSecondary,
  FormInput,
  IconX,
  ButtonIcon,
} from '@spotify-internal/encore-creator-web';
import { spacer24, spacer32, white } from '@spotify-internal/encore-foundation';
import { PreviewPanePodcast } from '../../../types';
import { copy } from '../../../vendor/Clipboard';
import { formatUriToUrl } from '../../../lib/Formatter';

const StyledHeader = styled(Type).attrs({
  forwardedAs: 'h1',
  variant: Type.display2,
})`
  padding-bottom: ${spacer32};
  @media (max-height: 750px) {
    font-size: 48px;
  }
`;

const StyledCopy = styled(Type).attrs({
  forwardedAs: 'p',
  variant: Type.heading4,
})`
  padding-bottom: ${spacer32};
`;

const StyledContainer = styled.div`
  padding-top: 500px;
  position: absolute;
  text-align: center;
  width: 100vw;
`;

const StyledFormInput = styled(FormInput)`
  width: 400px;
  display: inline-block;
  margin-right: ${spacer24};
`;

const StyledButtonIcon = styled(ButtonIcon)`
  position: absolute;
  top: ${spacer32};
  right: ${spacer32};
  z-index: 3;
  height: 24px;
  width: 24px;
`;

interface Props {
  location: {};
  history: {
    push: ({}) => void;
  };
  unsetSubmitFeedStatus: () => void;
  loadMe: () => void;
  rssFeed: PreviewPanePodcast | null;
  existingPodcast: boolean;
  submittedFeedUri: string;
}

export class Finished extends PureComponent<Props> {
  static propTypes = {
    location: PropTypes.shape({}).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func,
    }).isRequired,
    unsetSubmitFeedStatus: PropTypes.func.isRequired,
    loadMe: PropTypes.func.isRequired,
    existingPodcast: PropTypes.bool.isRequired,
    rssFeed: PropTypes.shape({
      isExistingShow: PropTypes.bool,
    }),
    submittedFeedUri: PropTypes.string,
  };

  static defaultProps = {
    rssFeed: {},
    submittedFeedUri: '',
  };

  state = {
    shareUriHasBeenCopied: false,
  };

  _timeout?: number = undefined;

  componentWillUnmount() {
    clearTimeout(this._timeout);
  }

  goToCatalog = () => {
    this.props.loadMe();
    this.props.unsetSubmitFeedStatus();
    const { history, location } = this.props;
    history.push({
      ...location,
      pathname: `/catalog`,
      search: null,
    });
  };

  get showUrl() {
    return formatUriToUrl(this.props.submittedFeedUri);
  }

  handleOnClick = (event: FormEvent<HTMLInputElement>) => {
    event.currentTarget.select();
  };

  handleCopyClick = () => {
    copy(this.showUrl).then(() => {
      this.setState({ shareUriHasBeenCopied: true });
      this._timeout = window.setTimeout(
        () => this.setState({ shareUriHasBeenCopied: false }),
        3000,
      );
    });
  };

  renderNewPodcastMessage = () => (
    <StyledContainer>
      <StyledButtonIcon onClick={this.goToCatalog} className="finished-button">
        <IconX aria-label="Close" color={white} />
      </StyledButtonIcon>
      <StyledHeader>
        <FormattedMessage
          id="add-podcast.finished-new-title"
          defaultMessage="Nice! We got your podcast."
        />
      </StyledHeader>
      <StyledCopy>
        <FormattedMessage
          id="add-podcast.finished-new-description"
          defaultMessage="It'll take a few hours to show up on Spotify. You can find your podcast here once it's ready"
        />
      </StyledCopy>
      <StyledFormInput value={this.showUrl} onClick={this.handleOnClick} readOnly />
      <ButtonSecondary
        buttonSize="md"
        onClick={this.handleCopyClick}
        disabled={this.state.shareUriHasBeenCopied}
        className="copy-button"
        theme={ButtonSecondary.darkTheme}
      >
        {this.state.shareUriHasBeenCopied ? (
          <FormattedMessage
            id="add-podcast.finished-new-copied-button"
            description="Button text after successfully copying a new podcast url"
            defaultMessage="Copied!"
          />
        ) : (
          <FormattedMessage
            id="add-podcast.finished-new-copy-button"
            description="Button text before copying a new podcast url"
            defaultMessage="Copy Link"
          />
        )}
      </ButtonSecondary>
    </StyledContainer>
  );

  renderExistingPodcastMessage = () => (
    <StyledContainer>
      <StyledHeader>
        <FormattedMessage id="add-podcast.finished-existing-title" defaultMessage="You’re in!" />
      </StyledHeader>
      <StyledCopy>
        <FormattedMessage
          id="add-podcast.finished-existing-description"
          defaultMessage="Now that you’ve added your podcast to Spotify, you can see powerful stats, updated daily."
        />
      </StyledCopy>
      <ButtonSecondary
        data-testid="finished-button"
        className="finished-button"
        onClick={this.goToCatalog}
        theme={ButtonSecondary.darkTheme}
      >
        <FormattedMessage
          id="add-podcast.finished-existing-button"
          description="Button text after successfully gaining access to an existing podcast"
          defaultMessage="See stats"
        />
      </ButtonSecondary>
    </StyledContainer>
  );

  render() {
    return (
      <section>
        {this.props.existingPodcast
          ? this.renderExistingPodcastMessage()
          : this.renderNewPodcastMessage()}
      </section>
    );
  }
}
